import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Search, Printer, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { DARK_INPUT, FilterSelect, Pager, Pill, Stat, errMsg, fmtDate, useDebounced } from "../control/shared";
import { WorkerDrawer } from "./WorkerDrawer";
import { PickupSettings } from "./PickupSettings";
import { UncollectedCards } from "./UncollectedCards";
import { CAT, blobUrl, catText, downloadBlob, printPdf } from "./util";

const SORTS = { "created_at:desc": "Newest first", "created_at:asc": "Oldest first", "name:asc": "Name A-Z", "name:desc": "Name Z-A", "city:asc": "City A-Z" };

const TABS = [
  ["pending", "Pending Applications"], ["approved", "Approved Workers"], ["rejected", "Rejected Workers"],
  ["suspended", "Suspended Workers"], ["ids", "Worker ID Management"], ["printing", "Physical ID Card Printing"],
];
const STATUS_OPTS = { approved: ["APPROVED", "CARD_PRINTED", "ISSUED"], suspended: ["SUSPENDED", "REVOKED"], ids: ["APPROVED", "CARD_PRINTED", "ISSUED", "SUSPENDED", "REVOKED"] };

function StatsRow({ s }) {
  if (!s) return null;
  const items = [["Total Registered", s.total, "text-slate-100"], ["Pending", s.pending, "text-amber-400"], ["Approved", s.approved, "text-emerald-400"],
    ["Rejected", s.rejected, "text-red-400"], ["Active", s.active, "text-emerald-300"], ["Suspended", s.suspended, "text-amber-300"],
    ["Cards Printed", s.cards_printed, "text-sky-400"], ["Cards Issued", s.cards_issued, "text-orange-400"]];
  return (
    <div className="grid grid-cols-2 sm:grid-cols-4 xl:grid-cols-8 gap-3" data-testid="wm-stats">
      {items.map(([l, v, t]) => <Stat key={l} label={l} value={v} tone={t} testid={`wm-stat-${l.toLowerCase().replace(/ /g, "-")}`}/>)}
    </div>
  );
}

export default function WorkerManagement() {
  const [tab, setTab] = useState("pending");
  const [stats, setStats] = useState(null);
  const [data, setData] = useState({ items: [], total: 0 });
  const [f, setF] = useState({ q: "", category: "", status: "", card: "", sort: "created_at", order: "desc", page: 1 });
  const [sel, setSel] = useState([]);
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);
  const q = useDebounced(f.q);

  const load = useCallback(() => {
    api.get("/admin/worker-mgmt/stats").then((r) => setStats(r.data)).catch(() => {});
    api.get("/admin/worker-mgmt/list", { params: { tab, ...f, q, page_size: 20 } }).then((r) => setData(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tab, f, q]);
  useEffect(() => { load(); }, [load]);
  const set = (k, v) => { setSel([]); setF((x) => ({ ...x, [k]: v, page: k === "page" ? v : 1 })); };
  const switchTab = (t) => { setTab(t); setSel([]); setF((x) => ({ ...x, status: "", card: "", page: 1 })); };

  const bulk = async (print) => {
    setBusy(true);
    try {
      const url = await blobUrl("/admin/worker-mgmt/cards/bulk.pdf", { method: "post", data: { user_ids: sel } });
      print ? printPdf(url) : downloadBlob(url, "karmivo-id-cards.pdf");
    } catch (e) { toast.error("Could not build the cards PDF"); } finally { setBusy(false); }
  };
  const all = data.items.length > 0 && data.items.every((i) => sel.includes(i.id));

  return (
    <div data-testid="worker-management">
      <div className="text-[10px] uppercase tracking-[0.25em] text-orange-400 font-bold">Workforce</div>
      <h1 className="font-display text-3xl font-bold mb-5">Worker Management</h1>
      <StatsRow s={stats}/>
      <div className="flex gap-1 overflow-x-auto mt-6 border-b border-slate-800" data-testid="wm-tabs">
        {TABS.map(([k, l]) => (
          <button key={k} onClick={() => switchTab(k)} data-testid={`wm-tab-${k}`}
            className={`px-3 py-2 text-sm font-semibold whitespace-nowrap border-b-2 transition-colors ${tab === k ? "border-orange-500 text-orange-300" : "border-transparent text-slate-400 hover:text-slate-200"}`}>{l}</button>
        ))}
      </div>
      <div className="flex flex-col md:flex-row gap-2 mt-4">
        <div className="relative flex-1"><Search size={14} className="absolute left-3 top-2.5 text-slate-500"/>
          <Input value={f.q} onChange={(e) => set("q", e.target.value)} placeholder="Search name, mobile, city or Worker ID" className={`${DARK_INPUT} h-9 pl-8`} data-testid="wm-search"/></div>
        <FilterSelect value={f.category} onChange={(v) => set("category", v)} options={Object.keys(CAT)} labels={CAT} placeholder="All categories" testid="wm-filter-category"/>
        {STATUS_OPTS[tab] && <FilterSelect value={f.status} onChange={(v) => set("status", v)} options={STATUS_OPTS[tab]} placeholder="All statuses" testid="wm-filter-status"/>}
        {tab === "printing" && <FilterSelect value={f.card} onChange={(v) => set("card", v)} options={["generated", "printed", "issued"]} placeholder="All cards" testid="wm-filter-card"/>}
        <FilterSelect value={`${f.sort}:${f.order}`} onChange={(v) => { const [s, o] = (v || "created_at:desc").split(":"); setF((x) => ({ ...x, sort: s, order: o, page: 1 })); }}
          options={Object.keys(SORTS)} labels={SORTS} placeholder="Newest first" testid="wm-sort"/>
      </div>
      {tab === "printing" && (
        <div className="flex flex-wrap items-center gap-2 mt-3" data-testid="wm-bulk-bar">
          <span className="text-xs text-slate-400">{sel.length} selected</span>
          <Button size="sm" className="bg-orange-500 hover:bg-orange-600" disabled={!sel.length || busy} onClick={() => bulk(true)} data-testid="wm-bulk-print">
            {busy ? <Loader2 size={14} className="animate-spin mr-1"/> : <Printer size={14} className="mr-1"/>}Bulk print</Button>
          <Button size="sm" variant="outline" className="border-slate-700 bg-transparent text-slate-300" disabled={!sel.length || busy} onClick={() => bulk(false)} data-testid="wm-bulk-download">Download PDF</Button>
          <span className="text-xs text-slate-500">Print at 100% / Actual size on CR80 (85.6 × 54 mm) cards.</span>
        </div>
      )}
      {tab === "printing" && <PickupSettings/>}
      {tab === "printing" && <UncollectedCards onOpen={setOpen} refreshKey={data}/>}
      <div className="admin-card mt-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="wm-table">
          <thead><tr className="text-left text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-800">
            {tab === "printing" && <th className="p-3 w-8"><Checkbox checked={all} onCheckedChange={(v) => setSel(v ? data.items.map((i) => i.id) : [])} data-testid="wm-select-all"/></th>}
            <th className="p-3">Worker</th><th className="p-3">Worker ID</th><th className="p-3">Categories</th><th className="p-3">City</th>
            <th className="p-3">Status</th><th className="p-3">{tab === "printing" || tab === "ids" ? "Card" : "Registered"}</th></tr></thead>
          <tbody>
            {data.items.map((w) => (
              <tr key={w.id} onClick={() => setOpen(w.id)} className="border-b border-slate-800/60 hover:bg-slate-800/40 cursor-pointer" data-testid={`wm-row-${w.id}`}>
                {tab === "printing" && <td className="p-3" onClick={(e) => e.stopPropagation()}><Checkbox checked={sel.includes(w.id)} onCheckedChange={(v) => setSel((s) => v ? [...s, w.id] : s.filter((x) => x !== w.id))} data-testid={`wm-select-${w.id}`}/></td>}
                <td className="p-3"><div className="font-semibold">{w.name}</div><div className="text-xs text-slate-500">{w.phone}</div></td>
                <td className="p-3 font-mono text-orange-300">{w.worker_code || "—"}</td>
                <td className="p-3 text-xs text-slate-300 max-w-[220px]">{catText(w.categories)}</td>
                <td className="p-3 text-slate-300">{w.city}{w.district && w.district !== w.city ? `, ${w.district}` : ""}</td>
                <td className="p-3"><Pill value={w.status}/></td>
                <td className="p-3 text-xs text-slate-400">{tab === "printing" || tab === "ids" ? (w.card ? <Pill value={w.card.status}/> : "—") : fmtDate(w.created_at)}</td>
              </tr>
            ))}
            {!data.items.length && <tr><td colSpan={7} className="p-8 text-center text-slate-500" data-testid="wm-empty">No workers here yet.</td></tr>}
          </tbody>
        </table>
      </div>
      <Pager page={f.page} pageSize={20} total={data.total} onPage={(p) => set("page", p)} testid="wm-pager"/>
      {open && <WorkerDrawer uid={open} onClose={() => setOpen(null)} onChanged={load}/>}
    </div>
  );
}
